const DB_NAME = "pos-local-db";
const STORE_NAME = "fnb-settings";
const DB_VERSION = 2;
const PRINT_SETTINGS_KEY = "print-settings";
const LOCAL_BACKUP_KEY = "POS_PRINT_SETTINGS";

export const PRINT_SETTINGS_UPDATED_EVENT = "print-settings-updated";

const DEFAULT_SETTINGS = {
  template_id: null,
  template_name: "",
  tt_id: null,
  receipt_template_id: null,
  receipt_template_name: "",
  receipt_tt_id: null,
  ticket_printer_name: "",
  receipt_printer_name: "",
};

let dbInstance = null;
let memoryCache = null;
let clearPatched = false;

function openDb() {
  return new Promise((resolve, reject) => {
    if (dbInstance) return resolve(dbInstance);

    if (!window.indexedDB) {
      reject(new Error("IndexedDB is not supported in this browser."));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = (event) => {
      dbInstance = event.target.result;
      resolve(dbInstance);
    };

    request.onerror = () => reject(request.error);
  });
}

function normalizeSettings(settings) {
  return {
    ...DEFAULT_SETTINGS,
    ...(settings && typeof settings === "object" ? settings : {}),
  };
}

function readLocalBackup() {
  try {
    const raw = window.localStorage.getItem(LOCAL_BACKUP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return normalizeSettings(parsed);
  } catch (e) {
    return null;
  }
}

function writeLocalBackup(settings) {
  try {
    window.localStorage.setItem(
      LOCAL_BACKUP_KEY,
      JSON.stringify(normalizeSettings(settings))
    );
  } catch (e) {
    console.warn("print settings local backup write failed", e);
  }
}

export function patchLocalStorageClearToPreservePrintSettings() {
  if (clearPatched || typeof window === "undefined" || !window.localStorage) {
    return;
  }
  clearPatched = true;
  const originalClear = window.localStorage.clear.bind(window.localStorage);
  window.localStorage.clear = function () {
    let backup = null;
    try {
      backup = window.localStorage.getItem(LOCAL_BACKUP_KEY);
    } catch (e) {
      console.warn("print settings backup read failed", e);
    }
    originalClear();
    if (backup) {
      try {
        window.localStorage.setItem(LOCAL_BACKUP_KEY, backup);
      } catch (e) {
        console.warn("print settings backup restore failed", e);
      }
    }
  };
}

export function getCachedPrintSettings() {
  if (memoryCache) {
    return normalizeSettings(memoryCache);
  }
  const backup = readLocalBackup();
  if (backup) {
    memoryCache = backup;
    return backup;
  }
  return normalizeSettings(null);
}

export async function getPrintSettings() {
  try {
    const db = await openDb();
    const stored = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(PRINT_SETTINGS_KEY);
      request.onsuccess = (event) => resolve(event.target.result || null);
      request.onerror = () => reject(request.error);
    });

    if (stored) {
      memoryCache = normalizeSettings(stored);
      writeLocalBackup(memoryCache);
      return memoryCache;
    }

    const backup = readLocalBackup();
    if (backup) {
      await savePrintSettings(backup);
      return getCachedPrintSettings();
    }

    memoryCache = normalizeSettings(null);
    return memoryCache;
  } catch (err) {
    console.log("getPrintSettings error", err);
    if (!memoryCache) {
      memoryCache = readLocalBackup() || normalizeSettings(null);
    }
    return getCachedPrintSettings();
  }
}

export async function savePrintSettings(settings) {
  const next = normalizeSettings(settings);
  memoryCache = next;
  writeLocalBackup(next);

  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      store.put(next, PRINT_SETTINGS_KEY);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch (err) {
    console.log("savePrintSettings error", err);
  }

  window.dispatchEvent(
    new CustomEvent(PRINT_SETTINGS_UPDATED_EVENT, {
      detail: next,
    })
  );

  return next;
}

export async function clearPrintSettings() {
  memoryCache = normalizeSettings(null);

  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      store.delete(PRINT_SETTINGS_KEY);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch (err) {
    console.log("clearPrintSettings error", err);
  }

  try {
    window.localStorage.removeItem(LOCAL_BACKUP_KEY);
  } catch (e) {
    console.warn("print settings backup remove failed", e);
  }

  window.dispatchEvent(
    new CustomEvent(PRINT_SETTINGS_UPDATED_EVENT, {
      detail: memoryCache,
    })
  );

  return true;
}

export async function initPrintSettingsCache() {
  patchLocalStorageClearToPreservePrintSettings();
  return getPrintSettings();
}



// WEBPACK FOOTER //
// ./src/util/indexDb/printSettingsIndexDb.js